/**
 * Category List Component
 * Hierarchical tree view of categories and subcategories with inline actions
 */

import { useState } from 'react';
import { ChevronDown, ChevronRight, FolderKanban, MoreVertical, Plus } from 'lucide-react';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '../ui/collapsible';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';
import { Button } from '../ui/button';
import { Skeleton } from '../ui/skeleton';
import type { Category, Subcategory } from '../../types/api';

interface CategoryListProps {
  categories: Category[];
  subcategories: Subcategory[];
  isLoading: boolean;
  onEditCategory: (category: Category) => void;
  onDeleteCategory: (category: Category) => void;
  onCreateSubcategory: (category: Category) => void;
  onEditSubcategory: (subcategory: Subcategory) => void;
  onDeleteSubcategory: (subcategory: Subcategory) => void;
}

export function CategoryList({
  categories,
  subcategories,
  isLoading,
  onEditCategory,
  onDeleteCategory,
  onCreateSubcategory,
  onEditSubcategory,
  onDeleteSubcategory,
}: CategoryListProps) {
  const [openCategories, setOpenCategories] = useState<Set<number>>(new Set());

  function toggleCategory(categoryId: number) {
    setOpenCategories((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(categoryId)) {
        newSet.delete(categoryId);
      } else {
        newSet.add(categoryId);
      }
      return newSet;
    });
  }

  function getSubcategoriesForCategory(categoryId: number): Subcategory[] {
    return subcategories.filter((sub) => sub.parent_category_id === categoryId);
  }

  if (isLoading) {
    return (
      <div className="space-y-2">
        {[1, 2, 3].map((i) => (
          <Skeleton key={i} className="h-16 w-full" />
        ))}
      </div>
    );
  }

  if (categories.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-12 text-center">
        <div className="mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-muted">
          <FolderKanban className="h-8 w-8 text-muted-foreground" />
        </div>
        <h3 className="mb-2 text-lg font-semibold">No categories yet</h3>
        <p className="text-sm text-muted-foreground">
          Create your first category to get started organizing assets.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-2">
      {categories.map((category) => {
        const categorySubcategories = getSubcategoriesForCategory(category.category_id);
        const isOpen = openCategories.has(category.category_id);

        return (
          <Collapsible
            key={category.category_id}
            open={isOpen}
            onOpenChange={() => toggleCategory(category.category_id)}
          >
            <div className="rounded-md border bg-card">
              <div className="flex items-center justify-between p-3">
                <div className="flex items-center gap-2 flex-1">
                  <CollapsibleTrigger asChild>
                    <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                      {isOpen ? (
                        <ChevronDown className="h-4 w-4" />
                      ) : (
                        <ChevronRight className="h-4 w-4" />
                      )}
                    </Button>
                  </CollapsibleTrigger>
                  <FolderKanban className="h-5 w-5 text-muted-foreground" />
                  <span className="font-semibold text-base">{category.category_name}</span>
                  <span className="text-xs text-muted-foreground">
                    ({categorySubcategories.length} subcategor{categorySubcategories.length === 1 ? 'y' : 'ies'})
                  </span>
                </div>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                      <MoreVertical className="h-4 w-4" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    <DropdownMenuItem onClick={() => onEditCategory(category)}>
                      Edit Category
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => onCreateSubcategory(category)}>
                      Add Subcategory
                    </DropdownMenuItem>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem
                      onClick={() => onDeleteCategory(category)}
                      className="text-destructive"
                    >
                      Delete Category
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>

              <CollapsibleContent>
                <div className="border-t bg-muted/30">
                  {categorySubcategories.length > 0 ? (
                    <div className="space-y-1 p-2">
                      {categorySubcategories.map((subcategory) => (
                        <div
                          key={subcategory.subcategory_id}
                          className="flex items-center justify-between rounded-md border bg-card p-2 pl-8"
                        >
                          <div className="flex items-center gap-2">
                            <span className="text-muted-foreground">└─</span>
                            <span className="text-sm">{subcategory.subcategory_name}</span>
                          </div>
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button variant="ghost" size="sm" className="h-7 w-7 p-0">
                                <MoreVertical className="h-3 w-3" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              <DropdownMenuItem onClick={() => onEditSubcategory(subcategory)}>
                                Edit Subcategory
                              </DropdownMenuItem>
                              <DropdownMenuSeparator />
                              <DropdownMenuItem
                                onClick={() => onDeleteSubcategory(subcategory)}
                                className="text-destructive"
                              >
                                Delete Subcategory
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="p-4 text-center text-sm text-muted-foreground">
                      No subcategories yet
                    </div>
                  )}
                  <div className="border-t p-2">
                    <Button
                      variant="outline"
                      size="sm"
                      className="w-full"
                      onClick={() => onCreateSubcategory(category)}
                    >
                      <Plus className="mr-2 h-4 w-4" />
                      Add Subcategory
                    </Button>
                  </div>
                </div>
              </CollapsibleContent>
            </div>
          </Collapsible>
        );
      })}
    </div>
  );
}
